<template>
  <Kanban
    class="project-leads-view-kanban"
    :columns="pipeline.columns"
    column-key="status"
    sort-key="priority"
    :items="items"
    :draggable="!Boolean(selectableColumn)"
    v-on="$listeners"
  >
    <template #item="{ item, column, index }">
      <Lead
        :item="item"
        :column="column"
        :reference="reference"
        :onModel="onModel"
        :variant="column === selectableColumn ? 'select' : 'default'"
        :properties="visibleProperties"
        v-on="$listeners"
      >
        <template v-if="selectableColumn === column" #primary-actions>
          <CheckboxComponent :value="selected.includes(item)" @input="e => onSelect(e, item, index)" />
        </template>
      </Lead>
    </template>
    <template #header="{ column, getItems }">
      <DropdownComponent class="project-leads-view-kanban__column-dropdown" :close-on-click="false">
        <template #default>
          <Badge v-if="column === selectableColumn" class="self-center mr-1">{{ selected.length }}</Badge>
          <ButtonComponent>
            <template #icon>
              <Icon icon="fa-pro-light:ellipsis-v" size="lg" />
            </template>
          </ButtonComponent>
        </template>
        <template #dropdown="{ close, visible }">
          <ButtonComponent
            v-if="!selectableColumn"
            :label="$tc('mark')"
            :icon="['fal', 'square']"
            @click="() => (selectableColumn = column) && close()"
          />
          <template v-if="selectableColumn">
            <ButtonComponent
              :label="$tc('selectAll')"
              :icon="['fal', 'square-check']"
              @click="selected = getItems(column)"
            />
            <ButtonComponent
              :label="$tc('cancel')"
              icon="fa-pro-light:times"
              @click="() => (selectableColumn = null) && close()"
            />

            <template v-if="selected.length">
              <strong>{{ $t('changeStatus') }}</strong>
              <ButtonComponent
                v-for="col in pipeline.columns"
                :label="col.label"
                icon="fa-pro-solid:circle"
                data-part="status-change"
                :style="`--circle-color: ${col.color};`"
                :promise="promises.status[col.id || 'default']"
                @click="() => onStatusChange(close, col.id)"
              />
              <hr />
              <ButtonComponent
                v-if="!selected.some(s => s.isArchived)"
                :label="$tc('archive')"
                :promise="promises.archive"
                icon="fa-pro-light:box-archive"
                confirm
                @click="() => archive(close)"
              />
              <ButtonComponent
                v-if="selected.some(s => s.isArchived)"
                :label="$tc('restore')"
                :promise="promises.unarchive"
                icon="fa-pro-light:undo"
                confirm
                @click="() => archive(close, true)"
              />
            </template>
          </template>
        </template>
      </DropdownComponent>
    </template>
  </Kanban>
</template>

<script>
import Kanban from '@/components/Kanban'
import Lead from '../items/Lead'
import Badge from '@/components/Badge'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'

export default {
  props: {
    items: Array,
    reference: String,
    onModel: String,
    pipeline: Object,
    visibleProperties: Array,
  },
  watch: {
    selectableColumn() {
      this.selected = []
      this.lastSelectedIndex = null
    },
  },
  data() {
    return {
      selectableColumn: null,
      selected: [],
      lastSelectedIndex: null,
      shiftPressed: false,
      promises: {
        archive: null,
        unarchive: null,
        status: {},
      },
    }
  },
  mounted() {
    this.onKeyDown = e => e.key === 'Shift' && (this.shiftPressed = true)
    this.onKeyUp = e => e.key === 'Shift' && (this.shiftPressed = false)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  },
  computed: {
    actions() {
      return []
    },
  },
  methods: {
    onSelect(_, item, index) {
      const isSelected = this.selected.includes(item)

      if (this.shiftPressed && this.lastSelectedIndex !== null && !isSelected) {
        const columnItems = this.items.filter(i => i.status === item.status)
        const start = Math.min(this.lastSelectedIndex, index)
        const end = Math.max(this.lastSelectedIndex, index)
        const range = columnItems.slice(start, end + 1)
        range.forEach(i => {
          if (!this.selected.includes(i)) this.selected.push(i)
        })
      } else if (isSelected) {
        this.selected = this.selected.filter(s => s !== item)
      } else {
        this.selected.push(item)
      }

      this.lastSelectedIndex = index
    },
    archive(close, restore = false) {
      const url = new URL(`/api/lead/${restore ? 'unarchive' : 'archive'}`, location.origin)
      url.searchParams.append(
        'ids',
        this.selected.map(s => s.id),
      )

      const key = restore ? 'unarchive' : 'archive'
      this.promises[key] = fetch(url.toString(), { method: 'POST' })
        .then(() => {
          this.selected.forEach(s => (s.isArchived = !restore))
          this.selectableColumn = null
        })
        .finally(() => close())
    },
    onStatusChange(close, status) {
      const url = new URL(`/api/lead/status`, location.origin)
      url.searchParams.append(
        'ids',
        this.selected.map(s => s.id),
      )
      if (status) url.searchParams.append('status', status)

      const promise = fetch(url.toString(), { method: 'POST' })
        .then(() => {
          this.selected.forEach(s => (s.status = status))
          this.selectableColumn = null
        })
        .finally(() => close())

      this.$set(this.promises.status, status || 'default', promise)
    },
  },
  components: {
    Kanban,
    Lead,
    DropdownComponent,
    CheckboxComponent,
    Badge,
  },
}
</script>

<style lang="scss">
.project-leads-view-kanban {
  &__column-dropdown {
    font-weight: normal;

    .elder-dropdown__trigger .elder-button {
      font-size: 0.8em;
    }
  }

  hr + hr {
    display: none;
  }

  .elder-checkbox:not(.elder-checkbox--checked) .elder-checkbox__box {
    background-color: white;
  }

  [data-part='status-change'] svg path {
    fill: var(--circle-color);
  }
}
</style>
